import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { GradingRow } from './GradingRow';

export const GradingTable = React.memo(({ title, data, sectionKey, onFieldChange }) => {
  const isComplexSection = sectionKey === 'basicFeatures' || sectionKey === 'bonus';
  const headerBg =
    sectionKey === 'deployment' ? '#e8f5e9' : sectionKey === 'bonus' ? '#fff3e0' : '#e3f2fd';

  return (
    <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
      <Box
        sx={{
          p: 2,
          bgcolor: headerBg,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" color="primary">
          {title}
        </Typography>
      </Box>
      {isComplexSection && (
        <Box sx={{ px: 2, pb: 1, pt: 0.5, bgcolor: headerBg }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Scoring:</strong> Functionality 80% + Aesthetics 20% (+10% bonus). Check the
            bonus box to let strong aesthetics compensate for minor functionality gaps (aesthetics
            weighted at 30% instead of 20%, capped at 100% of worth).
          </Typography>
        </Box>
      )}
      {sectionKey === 'bonus' && (
        <Box sx={{ px: 2, pb: 1, pt: 0.5, bgcolor: headerBg }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Optional extras:</strong> Same scoring as basic features (functionality +
            aesthetics). Points compensate for marks lost elsewhere — total is still capped at 200.
          </Typography>
        </Box>
      )}
      {sectionKey === 'deployment' && (
        <Box sx={{ px: 2, pb: 1, pt: 0.5, bgcolor: headerBg }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Hints:</strong> Use the info icon next to each item for the full grading
            description.
          </Typography>
        </Box>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="25%">Feature</TableCell>
            <TableCell width="8%">Worth</TableCell>
            {isComplexSection ? (
              <>
                <TableCell>Functionality</TableCell>
                <TableCell>Aesthetics</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Tooltip
                    title="Enable 150% aesthetics weighting (30% instead of 20%) to compensate for minor functionality gaps. Row score is still capped at 100% of worth."
                    arrow
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'help',
                      }}
                    >
                      Bonus
                      <InfoIcon sx={{ ml: 0.5, fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </Tooltip>
                </TableCell>
              </>
            ) : (
              <TableCell colSpan={3}>Score</TableCell>
            )}
            <TableCell width="20%">Comments</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <GradingRow
              key={idx}
              row={row}
              idx={idx}
              sectionKey={sectionKey}
              onFieldChange={onFieldChange}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
